import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  CreditCard, DollarSign, Users, Calendar, CheckCircle, 
  AlertCircle, Clock, Receipt, ArrowRight, Loader2, Settings,
  XCircle, RefreshCw, AlertTriangle
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Cancellation states
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationFeedback, setCancellationFeedback] = useState('');
  const [processingCancel, setProcessingCancel] = useState(false);
  const [cancellationStatus, setCancellationStatus] = useState(null);
  const [processingReactivate, setProcessingReactivate] = useState(false);

  const fetchBillingData = useCallback(async () => {
    try {
      const [billingRes, historyRes, stripeRes, cancelRes] = await Promise.all([
        axios.get(`${API}/billing/current`),
        axios.get(`${API}/billing/history`),
        axios.get(`${API}/billing/stripe-status`),
        axios.get(`${API}/billing/cancellation-status`).catch(() => ({ data: null }))
      ]);
      
      setBillingInfo(billingRes.data);
      setBillingHistory(historyRes.data.records);
      setStripeStatus(stripeRes.data);
      if (cancelRes.data) {
        setCancellationStatus(cancelRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    
    if (sessionId) {
      // Poll for payment status
      const pollStatus = async () => {
        setProcessingPayment(true);
        try {
          const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success('Payment successful! Your subscription is now active.');
            fetchBillingData();
          } else if (res.data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
          } else {
            // Keep polling
            setTimeout(pollStatus, 2000);
            return;
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
        setProcessingPayment(false);
      };
      pollStatus();
    } else if (cancelled) {
      toast.info('Payment cancelled');
    }
  }, [searchParams, fetchBillingData]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const handleSubscribe = async () => {
    try {
      setProcessingPayment(true);
      const res = await axios.post(`${API}/billing/checkout/session`, {}, {
        headers: { origin: window.location.origin }
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancellationReason) {
      toast.error('Please select a reason for cancellation');
      return;
    }
    
    setProcessingCancel(true);
    try {
      const res = await axios.post(`${API}/billing/cancel-subscription`, {
        reason: cancellationReason,
        feedback: cancellationFeedback
      });
      
      toast.success(res.data.message);
      setShowCancelDialog(false);
      setCancellationReason('');
      setCancellationFeedback('');
      fetchBillingData();
    } catch (error) {
      console.error('Cancellation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setProcessingCancel(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setProcessingReactivate(true);
    try {
      const res = await axios.post(`${API}/billing/reactivate-subscription`);
      toast.success(res.data.message);
      fetchBillingData();
    } catch (error) {
      console.error('Reactivation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setProcessingReactivate(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700">Trial</Badge>;
      case 'cancelling':
        return <Badge className="bg-orange-100 text-orange-700">Cancelling</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{status}</Badge>;
    }
  };

  const isCancelling = cancellationStatus?.is_cancelling;
  const isCancelled = cancellationStatus?.is_cancelled;
  const canReactivate = cancellationStatus?.can_reactivate;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing & Subscription</h1>
          <p className="text-slate-500">Manage your subscription and view billing history</p>
        </div>
      </div>

      {/* Cancellation Warning Banner */}
      {isCancelling && (
        <Card className="border-0 shadow-md bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">Subscription Cancellation Scheduled</h3>
                <p className="text-orange-700 text-sm mt-1">
                  Your subscription will end on{' '}
                  <strong>{cancellationStatus?.subscription_ends_at 
                    ? format(new Date(cancellationStatus.subscription_ends_at), 'dd/MM/yyyy')
                    : 'end of billing period'}</strong>.
                  {cancellationStatus?.days_remaining && (
                    <span> ({cancellationStatus.days_remaining} days remaining)</span>
                  )}
                </p>
                <p className="text-orange-600 text-sm mt-2">
                  You'll continue to have full access until then. Changed your mind?
                </p>
                <Button 
                  onClick={handleReactivateSubscription}
                  disabled={processingReactivate}
                  className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="reactivate-btn"
                >
                  {processingReactivate ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reactivate Subscription
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan Card */}
      <Card className={`border-0 shadow-lg ${isCancelling || isCancelled ? 'bg-gradient-to-br from-slate-500 to-slate-600' : 'bg-gradient-to-br from-indigo-500 to-purple-600'} text-white`}>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-indigo-100 text-sm mb-1">Current Plan</p>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold">${billingInfo?.total_monthly_cost?.toFixed(2)}</span>
                <span className="text-indigo-200">/month</span>
                {getStatusBadge(billingInfo?.subscription_status)}
              </div>
              <div className="mt-4 space-y-1 text-indigo-100">
                <p className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {billingInfo?.employee_count} employees
                </p>
                {billingInfo?.extra_employees > 0 && (
                  <p className="text-sm">
                    Base: ${billingInfo?.base_price} ({billingInfo?.base_employees} users) + 
                    ${billingInfo?.extra_cost?.toFixed(2)} ({billingInfo?.extra_employees} extra)
                  </p>
                )}
                {billingInfo?.next_billing_date && !isCancelling && (
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Next billing: {format(new Date(billingInfo.next_billing_date), 'dd/MM/yyyy')}
                  </p>
                )}
                {isCancelling && cancellationStatus?.subscription_ends_at && (
                  <p className="flex items-center gap-2 text-orange-200">
                    <Calendar className="w-4 h-4" />
                    Access ends: {format(new Date(cancellationStatus.subscription_ends_at), 'dd/MM/yyyy')}
                  </p>
                )}
              </div>
            </div>
            
            <div className="text-center md:text-right space-y-3">
              {billingInfo?.subscription_status === 'trial' && (
                <div className="space-y-3">
                  {stripeStatus?.configured ? (
                    <Button 
                      onClick={handleSubscribe}
                      disabled={processingPayment}
                      className="bg-white text-indigo-600 hover:bg-indigo-50"
                      data-testid="subscribe-btn"
                    >
                      {processingPayment ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Subscribe Now
                    </Button>
                  ) : (
                    <div className="bg-white/10 rounded-lg p-4">
                      <Settings className="w-6 h-6 mx-auto mb-2" />
                      <p className="text-sm">Payment system coming soon</p>
                    </div>
                  )}
                </div>
              )}
              {billingInfo?.subscription_status === 'active' && !isCancelling && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-200 justify-center md:justify-end">
                    <CheckCircle className="w-5 h-5" />
                    <span>Subscription Active</span>
                  </div>
                  <Button 
                    onClick={() => setShowCancelDialog(true)}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 text-sm"
                    data-testid="cancel-subscription-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                </div>
              )}
              {isCancelling && canReactivate && (
                <Button 
                  onClick={handleReactivateSubscription}
                  disabled={processingReactivate}
                  className="bg-white text-orange-600 hover:bg-orange-50"
                  data-testid="reactivate-header-btn"
                >
                  {processingReactivate ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reactivate
                </Button>
              )}
              {isCancelled && !canReactivate && (
                <div className="bg-white/10 rounded-lg p-4">
                  <XCircle className="w-6 h-6 mx-auto mb-2 text-red-300" />
                  <p className="text-sm">Subscription Ended</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="pt-6 text-center">
            <DollarSign className="w-8 h-8 mx-auto text-indigo-600 mb-2" />
            <p className="text-slate-500 text-sm">Base Price</p>
            <p className="text-2xl font-bold text-slate-900">${billingInfo?.base_price}/mo</p>
            <p className="text-slate-500 text-xs mt-1">Up to {billingInfo?.base_employees} employees</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-md">
          <CardContent className="pt-6 text-center">
            <Users className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-slate-500 text-sm">Your Employees</p>
            <p className="text-2xl font-bold text-slate-900">{billingInfo?.employee_count}</p>
            {billingInfo?.extra_employees > 0 && (
              <p className="text-slate-500 text-xs mt-1">
                {billingInfo?.extra_employees} over base ({billingInfo?.base_employees})
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-md">
          <CardContent className="pt-6 text-center">
            <Receipt className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <p className="text-slate-500 text-sm">Extra User Cost</p>
            <p className="text-2xl font-bold text-slate-900">
              ${billingInfo?.extra_cost?.toFixed(2) || '0.00'}
            </p>
            <p className="text-slate-500 text-xs mt-1">
              ${billingInfo?.extra_employee_price}/user/month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Billing Alert Info */}
      <Card className="border-0 shadow-md bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">How Billing Works</h3>
              <p className="text-blue-700 text-sm mt-1">
                Your monthly bill is calculated based on your active employee count. 
                The base price of ${billingInfo?.base_price}/month includes up to {billingInfo?.base_employees} employees. 
                Each additional employee costs ${billingInfo?.extra_employee_price}/month.
              </p>
              <p className="text-blue-700 text-sm mt-2">
                <strong>Example:</strong> With {billingInfo?.employee_count} employees, your bill is ${billingInfo?.total_monthly_cost?.toFixed(2)}/month.
                {billingInfo?.employee_count < billingInfo?.base_employees && (
                  <span> Adding {billingInfo?.base_employees - billingInfo?.employee_count} more employees won't change your bill.</span>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Billing History
          </CardTitle>
          <CardDescription>Your past invoices and payments</CardDescription>
        </CardHeader>
        <CardContent>
          {billingHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p>No billing history yet</p>
              <p className="text-sm">Your invoices will appear here once you subscribe</p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {billingHistory.map((record) => (
                  <div 
                    key={record.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {format(new Date(record.created_at), 'MMM yyyy')}
                      </p>
                      <p className="text-sm text-slate-500">
                        {record.employee_count} employees
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">${record.amount.toFixed(2)}</p>
                      <Badge 
                        className={record.status === 'paid' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                        }
                      >
                        {record.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Stripe Setup Notice */}
      {!stripeStatus?.configured && (
        <Card className="border-0 shadow-md bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Settings className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">Payment System Setup</h3>
                <p className="text-amber-700 text-sm mt-1">
                  The payment system is currently being configured. You'll be able to subscribe 
                  and manage your billing once setup is complete. Your trial will continue 
                  until then.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              We're sorry to see you go. Your subscription will remain active until the end of your current billing period.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Why are you cancelling? *</Label>
              <Select value={cancellationReason} onValueChange={setCancellationReason}>
                <SelectTrigger id="cancel-reason" data-testid="cancel-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="too_expensive">Too expensive</SelectItem>
                  <SelectItem value="not_using">Not using the service</SelectItem>
                  <SelectItem value="missing_features">Missing features I need</SelectItem>
                  <SelectItem value="switching_competitor">Switching to another service</SelectItem>
                  <SelectItem value="business_closed">Business closing/downsizing</SelectItem>
                  <SelectItem value="technical_issues">Technical issues</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cancel-feedback">Additional feedback (optional)</Label>
              <Textarea
                id="cancel-feedback"
                placeholder="Tell us how we could improve..."
                value={cancellationFeedback}
                onChange={(e) => setCancellationFeedback(e.target.value)}
                rows={3}
                data-testid="cancel-feedback-input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Grace Period:</strong> Your subscription will stay active until the end of your billing period. 
                You can reactivate at any time before then.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCancelDialog(false)}
              className="w-full sm:w-auto"
            >
              Keep Subscription
            </Button>
            <Button 
              onClick={handleCancelSubscription}
              disabled={processingCancel || !cancellationReason}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-cancel-btn"
            >
              {processingCancel ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
